import { gql } from "@/graphql/typed-gql"

export const GET_STUDENTS = gql`
  query GetStudents {
    students {
      id
      name
      age
      grade
      gradeKnown
      skillSetupDone
      notes
      # Has an address and has not confirmed it — so they are in no family yet
      # and cannot sign in. Without this the row looks like every other one.
      emailPending
      defaultExerciseCount
      suggestedExerciseCount
      avatarUrl
      sessionCount
      totalPoints
      currentStreak
      level {
        level
        name
        icon
      }
    }
  }
`

export const GET_STUDENT = gql`
  query GetStudent($id: ID!) {
    student(id: $id) {
      id
      name
      email
      hasPassword
      emailPending
      age
      birthYear
      birthMonth
      birthDay
      firstGradeYear
      grade
      gradeKnown
      skillSetupDone
      notes
      defaultExerciseCount
      suggestedExerciseCount
      # What a grown-up set per subject, and what a session there would be. Both,
      # because an empty box has to show the length it is opting out of.
      subjectSessionLengths {
        subjectKey
        subjectName
        count
        effectiveCount
      }
      requireDiscussionResolution
      avatarUrl
      totalPoints
      currentStreak
      longestStreak
      lastActivityAt
      sessions {
        id
        status
        createdAt
        completedAt
        subjectKey
        pointsEarned
        correctCount
        incorrectCount
        skippedCount
        totalCount
      }
      proficiencies {
        id
        skillKey
        proficiency
        isUnlocked
        totalAttempts
        correctAttempts
        skill {
          id
          name
          grade
          difficulty
          subjectId
          subjectName
          prerequisites
        }
      }
      achievements {
        id
        achievementKey
        unlockedAt
      }
      level {
        level
        name
        icon
        progressToNextLevel
        pointsToNextLevel
        nextLevel {
          name
        }
      }
    }
  }
`

export const GET_STUDENT_SKILLS = gql`
  query GetStudentSkills($id: ID!) {
    studentSkills(studentId: $id) {
      student {
        id
        name
        age
        grade
        gradeKnown
        skillSetupDone
      }
      learnerSubjects {
        id
        name
        icon
        active
        practised
        pinned
        hidden
        available
        recommended
        lastSolvedAt
        strands {
          key
          name
          description
          enabled
          skillCount
        }
      }
      subjects {
        id
        name
        proficiencies {
          id
          skillKey
          proficiency
          totalAttempts
          correctAttempts
          isUnlocked
          isPriority
          setupStatus
          manuallySet
          assumed
          skill {
            id
            name
            difficulty
            grade
            prerequisiteNames
            subjectId
            strand
          }
        }
      }
      # Only the rows that get a full card. Asked for separately because
      # conceptProgress reads the concept tag of every question file in a
      # skill's level directories, and nine in ten rows are all zeros and draw
      # a one-line row that shows none of this.
      liveProficiencies {
        id
        correctExerciseCount
        incorrectExerciseCount
        conceptsCovered
        conceptProgress {
          key
          name
          proficiency
          totalAttempts
          questionCount
          mastered
          untouched
        }
        history {
          id
          change
          reason
          createdAt
          session {
            id
            status
            completedAt
            createdAt
            subjectKey
            correctCount
            totalCount
            pointsEarned
            subject {
              id
              name
              icon
            }
          }
        }
      }
      unfinishedSessions {
        id
        subjectKey
        status
        updatedAt
      }
      priorityPlans {
        targetSkillKey
        targetSkillName
        subjectKey
        steps {
          skillKey
          skillName
          grade
          proficiency
          isUnlocked
          isTarget
        }
      }
    }
  }
`

export const GET_SKILL_SETUP_PROPOSAL = gql`
  query GetSkillSetupProposal($studentId: ID!, $subjects: [String!]) {
    skillSetupProposal(studentId: $studentId, subjects: $subjects) {
      skillKey
      subject
      subjectName
      grade
      name
      status
      currentStatus
      changed
      source
    }
  }
`

/** Everything a placement test's screen needs, in both states it has: waiting
    for the next answer, and finished with a finding to show. */
export const PLACEMENT_TEST_FIELDS = `
  id
  subject
  subjectName
  grade
  stage
  totalCount
  answeredCount
  completedAt
  stale
  knownCeiling
  claimedCount
  checkedCount
  correctCount
  gradeRows {
    grade
    probed
    claimed
    checked
    correct
    credited
  }
  question {
    id
    kind
    skillKey
    skillName
    grade
    level
    html
    hint
    choices
  }
`

export const GET_PLACEMENT_TESTS = gql`
  query GetPlacementTests($studentId: ID!) {
    placementTests(studentId: $studentId) {
      ${PLACEMENT_TEST_FIELDS}
    }
  }
`

export const GET_STUDENT_ACHIEVEMENTS = gql`
  query GetStudentAchievements($id: ID!) {
    studentAchievements(studentId: $id) {
      student {
        id
        name
        totalPoints
        currentStreak
        longestStreak
        lastActivityAt
      }
      level {
        level
        name
        icon
        progressToNextLevel
        pointsToNextLevel
        nextLevel {
          name
        }
      }
      achievements {
        id
        name
        description
        icon
        category
        points
        isUnlocked
        unlockedAt
      }
      totalAchievements
      unlockedCount
    }
  }
`

export const GET_STUDENT_AVATAR_CREDITS = gql`
  query GetStudentAvatarCredits($id: ID!) {
    studentAvatarCredits(studentId: $id) {
      imagesAvailable
    }
  }
`

export const GET_STUDENT_INCORRECT_EXERCISES = gql`
  query GetStudentIncorrectExercises($studentId: ID!, $skillKey: String) {
    student(id: $studentId) {
      id
      name
    }
    studentIncorrectExercises(studentId: $studentId, skillKey: $skillKey) {
      id
      exerciseKey
      skillKey
      skillName
      level
      result
      solvedAt
      sessionId
      problemHtml
      correctAnswer
      userAnswer
      hint
      choices
    }
  }
`

export const GET_STUDENT_AVATAR_GALLERY = gql`
  query GetStudentAvatarGallery($id: ID!) {
    studentAvatarGallery(studentId: $id) {
      id
      url
      isActive
      createdAt
    }
  }
`

/** Where an account stands on paying — plan, credits, and what it may buy. */
export const GET_BILLING_STATUS = gql`
  query BillingStatus($studentId: ID) {
    billingStatus(studentId: $studentId) {
      subscribed
      plan
      subscribedUntil
      aiCredits
      monthlyAiCredits
      metered
      paymentsEnabled
      stripeSubscription
      cancelAtPeriodEnd
      cancelable
      canManageBilling
      holderId
      offers {
        plan
        label
        mode
        interval
        amountCents
        currency
        credits
        consentText
        consentVersion
      }
      creditCosts {
        action
        label
        credits
      }
    }
  }
`
